import { useRef, useState } from "react";
import Modal from "@mui/material/Modal";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { BiUpload } from "react-icons/bi";
import toast from "react-hot-toast";
import { API } from "../../../api";
import {
  modalBoxStyle,
  headerStyle,
  headerTitleStyle,
  headerCloseButtonStyle,
  bodyStyle,
  subtitleStyle,
  uploadBoxStyle,
  uploadBoxActiveStyle,
  uploadIconWrapStyle,
  uploadFileNameStyle,
  uploadFileSizeStyle,
  uploadPlaceholderStyle,
  uploadHintStyle,
  fieldLabelStyle,
  textFieldSx,
  errorTextStyle,
  footerStyle,
  cancelButtonSx,
  confirmButtonSx,
} from "./style";

export default function PaymentProofModal({ open, onClose, onConfirm, label }) {
  const [file, setFile] = useState(null);
  const [referenceNumber, setReferenceNumber] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const handleClose = () => {
    if (uploading) return;
    setFile(null);
    setReferenceNumber("");
    setNote("");
    setError("");
    onClose();
  };

  const handleConfirm = async () => {
    if (!file && !referenceNumber.trim() && !note.trim()) {
      setError("At least one of payment proof, reference number, or note is required.");
      return;
    }

    let proofUrl = undefined;

    if (file) {
      try {
        setUploading(true);
        const res = await API.uploadFile(file);
        proofUrl = res?.data?.url;
        if (!proofUrl) throw new Error("Upload failed");
      } catch (err) {
        const msg = err?.data?.message || err?.message || "File upload failed.";
        setError(msg);
        toast.error(msg);
        setUploading(false);
        return;
      } finally {
        setUploading(false);
      }
    }

    onConfirm({
      payment_proof: proofUrl,
      reference_number: referenceNumber.trim() || undefined,
      payment_note: note.trim() || undefined,
    });
    handleClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalBoxStyle}>
        {/* Header */}
        <Box sx={headerStyle}>
          <Typography sx={headerTitleStyle}>{label}</Typography>
          <IconButton onClick={handleClose} sx={headerCloseButtonStyle} disabled={uploading}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={bodyStyle}>
          <Typography sx={subtitleStyle}>
            At least one of the following is required.
          </Typography>

          {/* Upload Area */}
          <Typography sx={fieldLabelStyle}>Payment Proof</Typography>
          <Box
            sx={file ? uploadBoxActiveStyle : uploadBoxStyle}
            onClick={() => !uploading && fileRef.current.click()}
          >
            <Box sx={uploadIconWrapStyle}>
              <BiUpload size={22} color="#126ABC" />
            </Box>
            {file ? (
              <>
                <Typography sx={uploadFileNameStyle}>{file.name}</Typography>
                <Typography sx={uploadFileSizeStyle}>
                  {(file.size / 1024).toFixed(1)} KB
                </Typography>
              </>
            ) : (
              <>
                <Typography sx={uploadPlaceholderStyle}>
                  Click to upload payment proof
                </Typography>
                <Typography sx={uploadHintStyle}>
                  PNG, JPG, PDF up to 1.5MB
                </Typography>
              </>
            )}
          </Box>
          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf"
            style={{ display: "none" }}
            onChange={(e) => {
              setFile(e.target.files[0] || null);
              setError("");
            }}
          />

          {/* Reference Number */}
          <Typography sx={{ ...fieldLabelStyle, mt: 2 }}>Reference Number</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter reference number"
            value={referenceNumber}
            onChange={(e) => { setReferenceNumber(e.target.value); setError(""); }}
            disabled={uploading}
            sx={{ ...textFieldSx, mb: 2 }}
          />

          {/* Note */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
            <Typography sx={fieldLabelStyle}>Note</Typography>
            <Typography sx={{ fontSize: "12px", color: note.length >= 1000 ? "#FF3850" : "#888", fontWeight: 500 }}>
              {note.length}/1000
            </Typography>
          </Box>
          <TextField
            fullWidth
            size="small"
            multiline
            rows={3}
            placeholder="Enter a note..."
            value={note}
            onChange={(e) => { 
              setNote(e.target.value); 
              setError(""); 
            }}
            inputProps={{ maxLength: 1000 }}
            disabled={uploading}
            sx={textFieldSx}
          />
          {note.length >= 1000 && (
            <Typography sx={{ color: "#FF3850", fontSize: "11px", mt: 0.5 }}>
              Note character limit reached (maximum 1000 characters).
            </Typography>
          )}

          {error && (
            <Typography sx={errorTextStyle}>{error}</Typography>
          )}
        </Box>

        {/* Footer */}
        <Box sx={footerStyle}>
          <Button variant="outlined" onClick={handleClose} disabled={uploading} sx={cancelButtonSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirm} disabled={uploading} sx={confirmButtonSx}>
            {uploading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} sx={{ color: "#fff" }} />
                <span>Uploading...</span>
              </Box>
            ) : (
              "Confirm"
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
